<template>
  <div :class="$style.favouriteElement">
    <lv-tooltip
      v-model:show-tooltip="showTooltip"
      :options="{placement: 'bottom-end'}"
      trigger="click"
      :is-button-automatic="false"
      styling="secondary-version2"
      :reference-with-overwrite="screenRange['desktop-medium-min'] ? 24 : 16"
    >
      <template #tooltip>
        <div :class="$style.wrapper">
          <template v-if="showRemovedFromList">
            <div :class="$style.removedElement">
              {{ $awt('aw.common.favourite.removed_from') }}
            </div>
            <div :class="$style.removedElement">
              {{ removedShoppingListName }}
            </div>
          </template>
          <template v-else>
            <div
              :class="$style.lastShoppingListName"
              v-html="$awt('aw.common.favourite.saved_to', {list_name: lastShoppingListName})"
            />
            <button
              v-show="!showMoreShoppingLists"
              type="button"
              :class="['buttonReset', $style.addToAnotherList]"
              @click="showMoreShoppingLists = !showMoreShoppingLists"
            >
              <span> {{ $awt('aw.common.favourite.add_to_another_list') }}</span>
              <lv-icon name="arrow-down-16" :size="16" />
            </button>
            <div v-show="showMoreShoppingLists" :class="$style.showMoreShoppingListsContainer">
              <div data-popover-item-wrapper>
                <div v-for="list in sortedShoppingLists" :key="list.id" data-popover-item>
                  <aw-checkbox-version2
                    v-model="list.isSelected"
                    has-label
                    @update:model-value="handleCheckboxChange(list, $event)"
                  >
                    <template #label>
                      {{ list.name }}
                    </template>
                  </aw-checkbox-version2>
                </div>
              </div>
            </div>

            <template v-if="showMoreShoppingLists">
              <aw-add-new-shopping-list-form
                v-show="showAddNewShoppingListForm"
                ref="addNewShoppingListForm"
                display-type="tooltip"
                @close-add-new-shopping-list-form="showAddNewShoppingListForm = false"
              />
              <lv-button
                v-show="!showAddNewShoppingListForm"
                ref="addNewShoppingListButton"
                styling="small-secondary-dark"
                :class="$style.addNewShoppingListButton"
                @click="showAddNewShoppingListFormCallback"
              >
                {{ $awt('aw.common.favourite.create_new_list') }}
              </lv-button>
            </template>
          </template>
        </div>
      </template>
      <template #reference>
        <button
          data-lv-tootip-reference
          type="button"
          :class="['buttonReset', $style.favButton, {[$style.favButtonMultiple]: isMultiple}]"
          :aria-disabled="isDisabled"
          :title="$awt('aw.common.favourite.add_to_favourite')"
          :aria-label="!isMultiple ? `${productName} ${ $awt('aw.common.favourite.label') }` : `${ $awt('aw.basket_tab.split_favourite') }`"
          @click="addToSavedBasket"
        >
          <lv-icon
            v-if="productData && !isMultiple"
            name="heart-version2-24"
            fill
            :size="24"
            :class="[$style.icon, {[$style.filled]: isFilled}]"
          />

          <template v-if="isMultiple">
            <span :class="$style.favButtonMultipleText" v-text="$awt('aw.basket_tab.split_favourite')" />
            <lv-icon
              name="transactions-16"
              fill
              :size="16"
              :class="$style.favButtonMultipleIconSmall"
            />
            <lv-icon
              name="transactions-16"
              fill
              :size="24"
              :class="$style.favButtonMultipleIconBig"
            />
          </template>
        </button>
      </template>
    </lv-tooltip>

    <aw-add-to-saved-basket-modal-version2
      :modal-type="modalTypeFav"
      :selected-value="selectedValue"
      :count="count"
      :analytics-favourite-group-type="analyticsFavouriteGroupType"
      :product-name="productName"
      :product-image="productImage"
      :show-removed-from-list="showRemovedFromList"
      :shopping-list-data="localShoppingListData"
      :product-id="productId"
      :variant-id="variantId"
      :product-data="productData"
      :is-multiple="isMultiple"
      :products="products"
      @modal-close="showRemovedFromList = false"
    />
  </div>
</template>

<script>
  import { mapState } from 'pinia';
  import { nextTick } from 'vue';
  import { LvIcon, LvTooltip, LvButton } from '~~/common/components/loginet-vue-shop/index';
  import { M_FAV_VERSION2 } from '~~/common/config/Modal';
  import { uuid4 } from '~~/common/utils';
  import userLoginAndRegistrationMixin from '~~/common/mixins/userLoginAndRegistrationMixin';

  import { useToasterStore } from '~~/common/stores/toaster';
  import { useModalStore } from '~~/common/stores/modal';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useProductsStore } from '~~/common/stores/products';
  import { useUserStore } from '~~/common/stores/user';
  import AwCheckboxVersion2 from '~~/common/components/Common/Widgets/AwCheckboxVersion2';
  import { unitNames } from '~~/common/utils/variant';
  import {
    PACKAGE_UNIT_PIECE,
    PACKAGE_UNIT_WEIGHT,
  } from '~~/common/config/packageUnit';
  import { setErrorNotification } from '~~/common/utils/helper';
  import { PUSH_ITEM_TO_FAVOURITE } from '~~/common/plugins/aw-analytics';
  import AwAddToSavedBasketModalVersion2 from '~~/common/components/Common/Modals/AwAddToSavedBasketModalVersion2';
  import listTypeEnum from '~~/shop/app/components/Page/Profile/SaveBasket/enum/listTypeEnum';
  import { useNuxtApp } from 'nuxt/app';
  import AwAddNewShoppingListForm from '../Page/Common/AwAddNewShoppingListForm.vue';

  export default {
    name: 'AwFavouriteVersion2',
    components: {
      AwAddNewShoppingListForm,
      AwAddToSavedBasketModalVersion2,
      LvButton,
      AwCheckboxVersion2,
      LvTooltip,
      LvIcon,
    },
    mixins: [userLoginAndRegistrationMixin],
    props: {
      variantId: {
        type: Number,
        required: false,
        default: 0,
      },
      productId: {
        type: Number,
        required: false,
        default: 0,
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      shoppingListData: {
        type: Array,
        default: () => {
          return [];
        },
      },
      selectedValue: {
        type: String,
        default: 'db',
      },
      count: {
        type: Number,
        default: 1,
      },
      productName: {
        type: String,
        required: false,
        default: '',
      },
      productData: {
        type: Object,
        required: false,
        default: () => ({}),
      },
      productImage: {
        type: Array,
        required: false,
        default: () => ([]),
      },
      isMultiple: {
        type: Boolean,
        default: false,
      },
      products: {
        type: Array,
        required: false,
        default: () => ([]),
      },
      defaultList: {
        type: String,
        default: listTypeEnum.FAVOURITE,
        validator: listType => Object.values(listTypeEnum).includes(listType),
      },
      needLoginMessageInToastNotification: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        modalTypeFav: M_FAV_VERSION2 + uuid4(),
        localShoppingListData: [],
        showTooltip: false,
        showRemovedFromList: false,
        lastShoppingListName: '',
        removedShoppingListName: '',
        showMoreShoppingLists: false,
        showAddNewShoppingListForm: false,
        analyticsFavouriteGroupType: {
          variantId: this.variantId,
        },
        toasterKey: null,
        loading: false,
        timer: null,
      };
    },
    computed: {
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      ...mapState(useUserStore, {
        isEcomUser: state => state.isEcomUser,
        shoppingLists: state => state.shoppingListNames ?? [],
        shoppingListSessionData: state => state.shoppingListSessionData ?? [],
      }),
      ...mapState(useModalStore, {
        confirmModal: state => state.modals.confirmModal,
      }),
      isFilled () {
        return this.localShoppingListData.length;
      },
      defaultShoppingListId () {
        return this.mappedShoppingLists.find(list => list.default)?.id;
      },
      existOkAuchanList () {
        return !!this.shoppingLists.find(list => list.listType === listTypeEnum.OKAUCHAN);
      },
      mappedShoppingLists () {
        return this.shoppingLists?.map(shoppingList => ({
          id: shoppingList.id,
          name: shoppingList.listType === listTypeEnum.FAVOURITE ? this.$awt('aw.profile.saved_baskets.default_list.title') : shoppingList.name,
          default: this.defaultList === listTypeEnum.OKAUCHAN && this.existOkAuchanList ? shoppingList.listType === listTypeEnum.OKAUCHAN : shoppingList.listType === listTypeEnum.FAVOURITE,
          isOkauchan: shoppingList.listType === listTypeEnum.OKAUCHAN,
          isSelected: this.localShoppingListData.some(listItem => String(listItem) === String(shoppingList.id)),
        })) ?? [];
      },
      sortedShoppingLists () {
        // sort the mappedShoppingLists by default, isSelected, then name
        return [...(this.mappedShoppingLists ?? [])]
          .sort(({
                   default: aDefault,
                   isSelected: aSelected,
                   name: aName,
                   isOkauchan: aIsOkauchan,
                 },
                 {
                   default: bDefault,
                   isSelected: bSelected,
                   name: bName,
                   isOkauchan: bIsOkauchan,
                 }) =>
            bDefault - aDefault || bIsOkauchan - aIsOkauchan || bSelected - aSelected || aName.localeCompare(bName),
          );
      },
      firstSelectedShoppingList () {
        return this.sortedShoppingLists.find(({ isSelected }) => isSelected);
      },
      productIds () {
        return !this.isMultiple
          ? [this.productId]
          : this.products.map((product) => {
            return product.variant.productId;
          });
      },
    },
    watch: {
      'confirmModal.confirmOk.type' (newValue) {
        if (newValue === 'addToSavedBasket') {
          this.openLoginModal();
        }
      },
      shoppingListData: {
        handler (newValue) {
          this.localShoppingListData = newValue;
        },
        deep: true,
      },
    },
    mounted () {
      this.localShoppingListData = !this.isMultiple
        ? [...this.shoppingListData]
        : this.calculateMultipleProductsShoppingListData()
      ;
    },
    methods: {
      calculateMultipleProductsShoppingListData () {
        const shoppingListsContains = this.products.map((product) => {
          return product.variant.shoppingListsContain;
        });

        const commonElements = new Set(shoppingListsContains[0]);

        for (let i = 1; i < shoppingListsContains.length; i++) {
          const currentSet = new Set(shoppingListsContains[i]);

          commonElements.forEach((element) => {
            if (!currentSet.has(element)) {
              commonElements.delete(element);
            }
          });
        }

        return Array.from(commonElements);
      },
      showAddNewShoppingListFormCallback () {
        this.showAddNewShoppingListForm = true;
        this.$refs.addNewShoppingListForm.showCallback();
      },
      toasterModifyCallback () {
        const modalStore = useModalStore();
        const toasterStore = useToasterStore();
        modalStore.showFavModal({ modalType: this.modalTypeFav });
        nextTick(() => {
          toasterStore.remove(this.toasterKey);
        });
      },
      async addProductToShoppingList (listId, refreshProductData = true) {
        const productsStore = useProductsStore();
        let anyError = false;

        let requestData = [];
        if (!this.isMultiple) {
          requestData = [{
            objectId: this.variantId + '',
            quantity: {
              quantity: this.count,
              type: {
                [PACKAGE_UNIT_PIECE]: 'piece',
                [PACKAGE_UNIT_WEIGHT]: 'weight',
              }[unitNames[this.selectedValue]],
            },
          }];
        } else {
          requestData = this.products.map((product) => {
            return {
              objectId: product.variant.id + '',
              quantity: {
                quantity: product.quantity.quantity,
                type: product.quantity.type,
              },
            };
          });
        }

        await this.$api.$post(`/me/shopping_lists/${listId}/product_variants`, { items: requestData })
          .catch((error) => {
            if (error?.response?.status === 400) {
              error.response.data.errors = [{
                ...error.response.data,
                message: '',
                hideErrorMsg: true,
              }];
              setErrorNotification({
                nuxtApp: useNuxtApp(),
                error,
                closable: false,
                forceModal: true,
              });
            } else {
              setErrorNotification({
                nuxtApp: useNuxtApp(),
                error,
              });
            }
            anyError = true;
            this.$logger.error(error);
          });

        try {
          if ('variantId' in this.analyticsFavouriteGroupType) {
            if (!this.isMultiple) {
              this.$awAnalytics[PUSH_ITEM_TO_FAVOURITE]({
                productData: this.productData,
                variantProperty: 'selectedVariant',
              });
            } else {
              this.products.forEach((product) => {
                this.$awAnalytics[PUSH_ITEM_TO_FAVOURITE]({
                  productData: product,
                  variantProperty: 'variant',
                });
              });
            }
          }
        } catch (error) {
          this.$logger.error(error);
        }

        // All OK: emit success
        if (!anyError) {
          this.localShoppingListData.push(listId);

          if (refreshProductData) {
            await productsStore.fetchUnCachedPartsReqAndUpdateStore({ productIds: this.productIds });
          }
        }
      },
      async removeProductFromShoppingList (listId) {
        const productsStore = useProductsStore();
        let anyError = false;

        const requestData = !this.isMultiple ?
          [{
            objectId: this.variantId + '',
            quantity: {
              quantity: 0,
            },
          }]
          : this.products.map((product) => {
            return {
              objectId: product.variant.id + '',
              quantity: {
                quantity: 0,
              },
            };
          });

        await this.$api.$put(`/me/shopping_lists/${listId}/product_variants`, { items: requestData })
          .catch((error) => {
            if (error?.response?.status === 400) {
              error.response.data.errors = [{
                ...error.response.data,
                message: '',
                hideErrorMsg: true,
              }];
              setErrorNotification({
                nuxtApp: useNuxtApp(),
                error,
                closable: false,
                forceModal: true,
              });
            } else {
              setErrorNotification({
                nuxtApp: useNuxtApp(),
                error,
              });
            }
            anyError = true;
            this.$logger.error(error);
          });

        // All OK: emit success
        if (!anyError) {
          this.localShoppingListData = this.localShoppingListData.filter(id => id !== listId);
          await productsStore.fetchUnCachedPartsReqAndUpdateStore({ productIds: this.productIds });
        }
      },
      findShoppingListById (id) {
        return this.mappedShoppingLists.find(list => list.id === String(id));
      },
      async handleCheckboxChange (list, checked) {
        const userStore = useUserStore();
        if (checked) {
          this.lastShoppingListName = list.name;
          await this.addProductToShoppingList(list.id);
          userStore.addShoppingListToSessionData(list.id);
        } else {
          await this.removeProductFromShoppingList(list.id);
          userStore.removeShoppingListFromSessionData(list.id);
          this.lastShoppingListName = this.firstSelectedShoppingList?.name ?? '-';
          if (this.lastShoppingListName === '-' && this.localShoppingListData.length === 0) {
            this.showRemovedFromList = true;
            this.removedShoppingListName = list.name;
          }
        }
      },
      async addProductToMultipleShoppingLists (listIds, refreshProductData = true) {
        const productId = this.productId;
        const variantId = this.variantId;
        let anyError = false;
        const productsStore = useProductsStore();
        try {
          if (!this.isMultiple) {
            await this.$api.post(
              `products/${productId}/variants/${variantId}/shopping_lists`,
              {
                items: listIds,
                quantity: {
                  quantity: this.count,
                  type: {
                    [PACKAGE_UNIT_PIECE]: 'piece',
                    [PACKAGE_UNIT_WEIGHT]: 'weight',
                  }[unitNames[this.selectedValue]],
                },
              },
            );
          } else {
            const itemsToSend = this.products.map((product) => {
              return {
                variantId: product.variant.id,
                listIds: listIds,
                quantity: {
                  quantity: product.quantity.quantity,
                  type: product.quantity.type,
                },
              };
            });

            await this.$api.post('/me/shopping_lists/product_variants', { items: itemsToSend });
          }
        } catch (error) {
          if (error?.response?.status === 400) {
            error.response.data.errors = [{
              ...error.response.data,
              message: '',
              hideErrorMsg: true,
            }];
            setErrorNotification({
              nuxtApp: useNuxtApp(),
              error,
              closable: false,
              forceModal: true,
            });
          } else {
            setErrorNotification({
              nuxtApp: useNuxtApp(),
              error,
            });
          }
          anyError = true;
          this.$logger.error(error);
        }
        try {
          if ('variantId' in this.analyticsFavouriteGroupType) {
            if (!this.isMultiple) {
              this.$awAnalytics[PUSH_ITEM_TO_FAVOURITE]({
                productData: this.productData,
                variantProperty: 'selectedVariant',
              });
            } else {
              this.products.forEach(product => {
                this.$awAnalytics[PUSH_ITEM_TO_FAVOURITE]({
                  productData: product,
                  variantProperty: 'variant',
                });
              });
            }
          }
        } catch (error) {
          this.$logger.error(error);
        }
        // All OK: emit success
        if (!anyError) {
          listIds.forEach((listId) => {
            this.localShoppingListData.push(listId);
          });
          if (refreshProductData) {
            await productsStore.fetchUnCachedPartsReqAndUpdateStore({ productIds: this.productIds });
          }
        }
      },
      async initDesktopPopover () {
        clearTimeout(this.timer);

        if (this.localShoppingListData.length === 0) {
          if (this.shoppingListSessionData.length > 0) {
            await this.addProductToMultipleShoppingLists(this.shoppingListSessionData);
          } else {
            await this.addProductToShoppingList(this.defaultShoppingListId);
            const userStore = useUserStore();
            userStore.addShoppingListToSessionData(this.defaultShoppingListId);
          }
          this.showTooltip = true;
          this.showMoreShoppingLists = this.shoppingListSessionData.length > 1;
          this.showRemovedFromList = false;
          this.lastShoppingListName = this.firstSelectedShoppingList?.name;
        } else if (this.localShoppingListData.length === 1 && !this.isMultiple) {
          const list = this.findShoppingListById(this.localShoppingListData[0]);
          await this.removeProductFromShoppingList(this.localShoppingListData[0]);
          this.showTooltip = true;
          this.showRemovedFromList = true;
          this.removedShoppingListName = list?.name || '-';
          this.timer = setTimeout(() => {
            this.showTooltip = false;
            this.showRemovedFromList = false;
            clearTimeout(this.timer);
          }, 10 * 1000);
        } else if (this.localShoppingListData.length > 0 && this.isMultiple) {
          this.showTooltip = !this.showTooltip;
          if (this.showTooltip) {
            this.showMoreShoppingLists = true;
          }
        } else {
          this.showRemovedFromList = false;
          this.showTooltip = true;
          this.showMoreShoppingLists = true;
          this.lastShoppingListName = this.firstSelectedShoppingList?.name;
        }
      },
      async initMobilePopover () {
        const toasterStore = useToasterStore();

        if (this.localShoppingListData.length === 0) {
          if (this.shoppingListSessionData.length > 0) {
            await this.addProductToMultipleShoppingLists(this.shoppingListSessionData);
          } else {
            await this.addProductToShoppingList(this.defaultShoppingListId);
            const userStore = useUserStore();
            userStore.addShoppingListToSessionData(this.defaultShoppingListId);
          }

          this.lastShoppingListName = this.firstSelectedShoppingList?.name;

          this.toasterKey = toasterStore.push({
            title: this.$awt('aw.common.favourite.toaster.title'),
            text: this.$awt('aw.common.favourite.toaster.text', { list_name: this.lastShoppingListName }),
            manualClose: true,
            screenReaderOnly: false,
            showModifyButton: true,
            modifyButtonCallback: this.toasterModifyCallback,
          });
        } else if (this.localShoppingListData.length === 1 && !this.isMultiple) {
          const list = this.findShoppingListById(this.localShoppingListData[0]);
          await this.removeProductFromShoppingList(this.localShoppingListData[0]);
          this.removedShoppingListName = list?.name || '-';
          const key = toasterStore.push({
            title: this.$awt('aw.common.favourite.remove_toaster.title'),
            text: this.$awt('aw.common.favourite.remove_toaster.text', { list_name: this.removedShoppingListName }),
            manualClose: true,
            screenReaderOnly: false,
            showModifyButton: false,
            modifyButtonCallback: () => ({}),
          });
          setTimeout(() => {
            toasterStore.remove(key);
          }, 10 * 1000);
        } else {
          const modalStore = useModalStore();
          modalStore.showFavModal({ modalType: this.modalTypeFav });
          this.showRemovedFromList = !this.isMultiple;
        }
      },
      async initShoppingListNames () {
        const userStore = useUserStore();
        await userStore.fetchShoppingListNames();
      },
      async addToSavedBasket () {
        if (!this.isDisabled && !this.loading) {
          this.loading = true;
          const modalStore = useModalStore();

          if (this.isEcomUser) {
            await this.initShoppingListNames();
            if (this.screenRange['tablet-min']) {
              await this.initDesktopPopover();
            } else {
              await this.initMobilePopover();
            }
          } else {
            if (this.needLoginMessageInToastNotification) {
              const toasterStore = useToasterStore();
              toasterStore.push({
                manualClose: false,
                title: this.$awt('aw.common.favourite.login_modal.text'),
              });
            } else {
              modalStore.showConfirmModal({
                closable: true,
                data: {
                  confirmTitle: this.$awt('aw.common.favourite.login_modal.title'),
                  confirmText: this.$awt('aw.common.favourite.login_modal.text'),
                  confirmOkButton: this.$awt('aw.common.favourite.login_modal.login'),
                  confirmOkButtonAttrs: {},
                  confirmCancelButton: this.$awt('aw.global.cancel'),
                  confirmOkButtonStyle: 'primary-dark',
                  confirmCancelButtonStyle: 'secondary-dark',
                  showRegistrationFooter: true,
                  registrationFooterCallback: () => {
                    this.openLoginModal();
                  },
                  confirmOkActionType: 'addToSavedBasket',
                },
              });
            }
          }
          this.loading = false;
        }
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
.icon {
  --color1: #{$color-white};
  stroke-width: 1.5;
}

.filled {
  --color1: #{$color-strawberry-450};
  --color2: #{$color-strawberry-300};
}

.favButton {
  &:focus-visible {
    border-radius: 8px;
    box-shadow: 0 0 0 2px $color-strawberry-450;
  }

  &Multiple {
    display: flex;
    align-items: center;
    text-align: left;
    text-decoration: underline;
    border-radius: 8px;
    gap: 4px;

    @include desktop-medium(min) {
      text-decoration: none;
      gap: 8px;
    }

    &:focus-visible {
      outline: 2px solid $color-black;
    }

    &Text {
      @include mobile(max) {
        display: none;
      }
    }

    &Icon {
      &Small,
      &Big {
        flex-shrink: 0;
      }

      &Small {
        @include desktop-medium(min) {
          display: none;
        }
      }

      &Big {
        @include desktop-small(max) {
          display: none;
        }
      }
    }
  }
}

.wrapper {
  width: 200px;
  padding: 8px;
  border-radius: 12px;
  background: $color-background-4;

  [data-popover-item-wrapper] {
    width: auto !important;
    padding: 0;
  }

}

.favouriteElement {
  [data-popper-element] {
    max-height: 300px;
    padding: 0;

    [data-popover-item-wrapper] {
      &::-webkit-scrollbar {
        width: 12px;
        height: 6px;
      }

      &::-webkit-scrollbar-track {
        border-radius: 36px;
        background: $color-background-4;
      }

      &::-webkit-scrollbar-thumb {
        border: 3px solid transparent;
        border-radius: 36px;
        background-color: $color-border-disabled;
        background-clip: content-box;

        &:hover,
        &:active {
          background-color: $color-border-disabled;
        }
      }
    }
  }

  .lastShoppingListName,
  .removedElement {
    font-size: 14px;
    font-weight: $font-weight-normal-v2;
    line-height: 20px;
    padding: 8px;
    text-align: left;
    word-break: break-word;
    hyphens: auto;

    u {
      font-weight: $font-weight-medium-v2;
    }
  }

  .addToAnotherList {
    font-size: 14px;
    font-weight: $font-weight-normal-v2;
    line-height: 20px;

    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    padding: 8px;
    gap: 8px;
  }

  .showMoreShoppingListsContainer {
    overflow: hidden;
    padding: 0;

    [data-popover-item-wrapper] {
      overflow: hidden auto;
    }
  }

  .addNewShoppingListButton {
    margin: 2px 0;
  }
}

</style>
